import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import propertyMetersService from '../services/propertyMetersService';
import { METER_TYPES } from '../constants/meterTypes';
import styles from '../assets/manageRoomsModal.module.css';

// Manage the property's meter register.
//
// Meters are added, corrected, replaced and removed HERE only -- never from a
// monthly inspection report, where the inspector supplies just the reading and
// a photo. Every meter listed here is loaded automatically onto each new
// monthly inspection report.
const EMPTY_FORM = { meter_type: '', serial_number: '', location: '' };

// `addOnly` renders just the Add form -- the ADD METER buttons use it, while
// the pencil icon opens the full register for editing, replacing and removing.
// Mirrors the AddRoomModal / ManageRoomsModal split already on this page.
const ManageMetersModal = ({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  onMetersChange,
  addOnly = false,
}) => {
  const [meters, setMeters] = useState([]);
  // Some properties genuinely have no meters. Without an explicit record of
  // that, they would be blocked from monthly inspections indefinitely.
  const [noMeters, setNoMeters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Which row is being edited/replaced, and the form backing it. `mode` is
  // 'add' | 'edit' | 'replace' -- edit and replace look similar but mean very
  // different things (see handleSave).
  const [mode, setMode] = useState('add');
  const [activeMeterId, setActiveMeterId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const loadMeters = useCallback(async () => {
    if (!propertyId) return;

    setLoading(true);
    try {
      const response = await propertyMetersService.getMetersByProperty(propertyId);
      if (response.data?.success) {
        setMeters(response.data.data || []);
        setNoMeters(Boolean(response.data.no_meters_confirmed));
      } else {
        toast.error(response.data?.message || 'Failed to load meters');
        setMeters([]);
      }
    } catch (error) {
      console.error('Error loading meters:', error);
      toast.error('Failed to load meters');
      setMeters([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (isOpen) {
      loadMeters();
      resetForm();
    }
  }, [isOpen, propertyId, loadMeters]);

  const resetForm = () => {
    setMode('add');
    setActiveMeterId(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  // Mirrors the server-side rules in propertymetersController.validateMeterPayload
  // so the user sees the problem before a round trip.
  const validate = () => {
    const next = {};
    if (!form.meter_type) next.meter_type = 'Meter type is required';
    if (!form.serial_number.trim()) next.serial_number = 'Serial number is required';
    if (!form.location.trim()) next.location = 'Location is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleStartEdit = (meter) => {
    setMode('edit');
    setActiveMeterId(meter.id);
    setForm({
      meter_type: meter.meter_type || '',
      serial_number: meter.serial_number || '',
      location: meter.location || '',
    });
    setErrors({});
  };

  // Replacement pre-fills type and location (usually unchanged) but clears the
  // serial, since a new physical unit is the whole point.
  const handleStartReplace = (meter) => {
    setMode('replace');
    setActiveMeterId(meter.id);
    setForm({
      meter_type: meter.meter_type || '',
      serial_number: '',
      location: meter.location || '',
    });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      meter_type: form.meter_type,
      serial_number: form.serial_number.trim(),
      location: form.location.trim(),
    };

    setSaving(true);
    try {
      let response;
      if (mode === 'add') {
        response = await propertyMetersService.addMeter(propertyId, payload);
      } else if (mode === 'edit') {
        // A correction: propagates to every report showing this meter.
        response = await propertyMetersService.updateMeter(activeMeterId, payload);
      } else {
        // A physical swap: the old meter is retired, reports before the swap
        // keep showing it.
        response = await propertyMetersService.replaceMeter(activeMeterId, payload);
      }

      if (response.data?.success) {
        toast.success(
          mode === 'add'
            ? 'Meter added'
            : mode === 'edit'
            ? 'Meter updated'
            : 'Meter replaced'
        );
        resetForm();
        await loadMeters();
        if (onMetersChange) onMetersChange();
        // Adding from a dedicated button is a one-shot action: close rather
        // than leave an empty form sitting there.
        if (addOnly) onClose();
      } else {
        setErrors(response.data?.errors || {});
        toast.error(response.data?.message || 'Failed to save meter');
      }
    } catch (error) {
      console.error('Error saving meter:', error);
      // Validation failures come back as 400 with a field->message map.
      const apiErrors = error.response?.data?.errors;
      if (apiErrors) {
        setErrors(apiErrors);
        toast.error(error.response.data.message || 'Please correct the errors below');
      } else {
        toast.error('Failed to save meter');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleNoMetersToggle = async (checked) => {
    setSaving(true);
    try {
      const response = await propertyMetersService.setNoMeters(propertyId, checked);
      if (response.data?.success) {
        setNoMeters(checked);
        toast.success(
          checked
            ? "Recorded: this property has no meters"
            : "No longer marked as having no meters"
        );
        if (onMetersChange) onMetersChange();
      } else {
        toast.error(response.data?.message || "Could not update this property");
      }
    } catch (error) {
      console.error("Error setting no-meters flag:", error);
      toast.error(
        error.response?.data?.message || "Could not update this property"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (meter) => {
    const confirmed = window.confirm(
      `Remove the ${meter.meter_type} meter (serial ${meter.serial_number})?\n\n` +
        'It will stop appearing on new monthly inspection reports. ' +
        'Existing reports keep their record of it.'
    );
    if (!confirmed) return;

    try {
      const response = await propertyMetersService.removeMeter(meter.id);
      if (response.data?.success) {
        toast.success('Meter removed');
        resetForm();
        await loadMeters();
        if (onMetersChange) onMetersChange();
      } else {
        toast.error(response.data?.message || 'Failed to remove meter');
      }
    } catch (error) {
      console.error('Error removing meter:', error);
      toast.error('Failed to remove meter');
    }
  };

  if (!isOpen) return null;

  const formTitle =
    mode === 'add'
      ? 'Add meter'
      : mode === 'edit'
      ? 'Correct meter details'
      : 'Replace meter';

  return (
    <div className={styles['modal-overlay']} onClick={onClose}>
      <div
        className={styles['manage-rooms-modal']}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles['modal-header']}>
          <h4 className={styles['modal-title']}>
            {addOnly ? 'Add meter' : 'Meter details'}
            {propertyName ? ` — ${propertyName}` : ''}
          </h4>
          <button
            type="button"
            className={styles['btn-modal-close']}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className={styles['modal-body']}>
          {addOnly ? null : loading ? (
            <div className="text-center">
              <div className={styles['spinner-border']}></div>
              <p>Loading meters...</p>
            </div>
          ) : (
            <table className={styles['table']}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Serial number</th>
                  <th>Location</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {meters.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', color: '#6c757d' }}>
                      No meters added yet. Meters added here appear on every new
                      monthly inspection report.
                    </td>
                  </tr>
                ) : (
                  meters.map((meter) => (
                    <tr key={meter.id}>
                      <td>{meter.meter_type}</td>
                      <td>{meter.serial_number}</td>
                      <td>{meter.location}</td>
                      <td>
                        <button
                          type="button"
                          className={`${styles['btn']} ${styles['btn-sm']} ${styles['btn-info']}`}
                          onClick={() => handleStartEdit(meter)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={`${styles['btn']} ${styles['btn-sm']} ${styles['btn-info']}`}
                          onClick={() => handleStartReplace(meter)}
                          title="The physical meter has been swapped for a new unit"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className={`${styles['btn']} ${styles['btn-sm']} ${styles['btn-danger']}`}
                          onClick={() => handleRemove(meter)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {!addOnly && meters.length === 0 && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: '#fcf8e3',
                border: '1px solid #faebcc',
                borderRadius: 4,
              }}
            >
              <label style={{ fontWeight: 'normal', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={noMeters}
                  disabled={saving}
                  onChange={(e) => handleNoMetersToggle(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                This property has no meters
              </label>
              <div style={{ fontSize: 12, color: '#8a6d3b', marginTop: 6 }}>
                Tick this only if there genuinely are no gas, electric or water
                meters here. Monthly inspections are blocked while a property has
                neither meters nor this confirmation.
              </div>
            </div>
          )}

          <div
            style={
              addOnly
                ? {}
                : { borderTop: '1px solid #dee2e6', marginTop: 16, paddingTop: 16 }
            }
          >
            {!addOnly && <h5 style={{ marginBottom: 4 }}>{formTitle}</h5>}
            {mode === 'replace' && (
              <p style={{ fontSize: 13, color: '#6c757d', margin: '0 0 10px' }}>
                Use this when the physical meter has been swapped. The old meter
                is retired and stays on reports from before the swap.
              </p>
            )}
            {mode === 'edit' && (
              <p style={{ fontSize: 13, color: '#6c757d', margin: '0 0 10px' }}>
                Use this to correct a mistake. The correction shows on every
                report that displays this meter.
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label>
                  Meter type <span style={{ color: '#d9534f' }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={form.meter_type}
                  onChange={(e) => handleFieldChange('meter_type', e.target.value)}
                >
                  <option value="">Select type</option>
                  {METER_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.meter_type && (
                  <small style={{ color: '#d9534f' }}>{errors.meter_type}</small>
                )}
              </div>

              <div style={{ flex: '1 1 180px' }}>
                <label>
                  Serial number <span style={{ color: '#d9534f' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  maxLength={255}
                  value={form.serial_number}
                  onChange={(e) => handleFieldChange('serial_number', e.target.value)}
                />
                {errors.serial_number && (
                  <small style={{ color: '#d9534f' }}>{errors.serial_number}</small>
                )}
              </div>

              <div style={{ flex: '2 1 220px' }}>
                <label>
                  Location <span style={{ color: '#d9534f' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  maxLength={255}
                  placeholder="e.g. Basement cupboard, left of the door"
                  value={form.location}
                  onChange={(e) => handleFieldChange('location', e.target.value)}
                />
                {errors.location && (
                  <small style={{ color: '#d9534f' }}>{errors.location}</small>
                )}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className={`${styles['btn']} ${styles['btn-info']}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? 'Saving...'
                  : mode === 'add'
                  ? 'Add meter'
                  : mode === 'edit'
                  ? 'Save changes'
                  : 'Replace meter'}
              </button>
              {mode !== 'add' && (
                <button
                  type="button"
                  className={`${styles['btn']} ${styles['btn-default']}`}
                  onClick={resetForm}
                  disabled={saving}
                  style={{ marginLeft: 8 }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles['modal-footer']}>
          <button
            type="button"
            className={`${styles['btn-default']} ${styles['btn-modal-close']}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageMetersModal;
